import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Card, 
  Row, 
  Col, 
  Statistic, 
  Tag, 
  Space, 
  Button, 
  Typography, 
  Descriptions, 
  Tabs, 
  Table, 
  Progress, 
  Alert,
  Timeline,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  message,
  Badge,
  Tooltip,
  List
} from 'antd';
import { safeFormatTimestamp } from '../../utils/timezone';
import {
  ArrowLeftOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  UserOutlined,
  MailOutlined,
  CalendarOutlined,
  LockOutlined,
  KeyOutlined,
  HistoryOutlined,
  SettingOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  SecurityScanOutlined,
  TeamOutlined,
  BarChartOutlined,
  FileTextOutlined,
  GlobalOutlined
} from '@ant-design/icons';
import axios from 'axios';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip as RechartsTooltip } from 'recharts';
import { useAuth } from '../../contexts/AuthContext';
import { handleApiError } from '../../utils/errorHandler';
import {
  usernameRules,
  emailRules,
  fullNameRules,
  passwordRules,
  passwordHelpText,
} from '../../utils/validationRules';
import logger from '../../services/logger';

const { Title, Text } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;

const UserProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activityLogs, setActivityLogs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [availableCustomers, setAvailableCustomers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [passwordPolicy, setPasswordPolicy] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [passwordModalVisible, setPasswordModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [passwordForm] = Form.useForm();

  useEffect(() => {
    fetchUserData();
    fetchRoles();
    fetchAvailableCustomers();
    fetchPasswordPolicy();
    // Auto-refresh every 60 seconds
    const interval = setInterval(fetchUserData, 60000);
    return () => clearInterval(interval);
  }, [id]);

  const fetchRoles = async () => {
    try {
      const response = await axios.get('/api/v1/roles');
      setRoles(response.data || []);
    } catch (error) {
      logger.warn('Roller yüklenemedi:', error);
    }
  };

  const fetchAvailableCustomers = async () => {
    try {
      const response = await axios.get('/api/v1/customers');
      setAvailableCustomers(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      logger.warn('Müşteriler yüklenemedi:', error);
    }
  };

  const fetchPasswordPolicy = async () => {
    try {
      const response = await axios.get('/api/v1/profile/password-requirements');
      setPasswordPolicy(response.data);
    } catch (error) {
      logger.error('Password policy fetch error:', error);
    }
  };

  const fetchUserData = async () => {
    try {
      setLoading(true);

      // Fetch user details
      const userResponse = await axios.get(`/api/v1/users/${id}`);
      setUser(userResponse.data);

      // Fetch activity logs from audit API
      try {
        const logsResponse = await axios.get(`/api/v1/profile/audit?user_id=${id}`);
        setActivityLogs(logsResponse.data.logs || []);
      } catch (error) {
        logger.warn('Activity logs yüklenemedi:', error);
        setActivityLogs([]);
      }

      // If user is customer role, fetch assigned customers
      if (userResponse.data.role?.name === 'customer') {
        fetchUserCustomers();
      }

    } catch (error) {
      message.error('Kullanıcı bilgileri yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserCustomers = async () => {
    try {
      setCustomersLoading(true);
      const response = await axios.get(`/api/v1/users/${id}`);
      // The customers field is already included in the user response
      setCustomers(response.data.customers || []);
    } catch (error) {
      logger.error('Müşteriler yüklenirken hata:', error);
      message.error('Müşteriler yüklenirken hata oluştu');
      setCustomers([]);
    } finally {
      setCustomersLoading(false);
    }
  };


  const getRoleTag = (role) => {
    if (!role) return <Tag>-</Tag>;
    const roleName = typeof role === 'object' ? role.display_name || role.name : role;
    const roleColor = typeof role === 'object' ? role.color : null;
    return <Tag color={roleColor || 'default'}>{roleName}</Tag>;
  };

  const getActionIcon = (action) => {
    const icons = {
      login: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
      logout: <ClockCircleOutlined style={{ color: '#fa8c16' }} />,
      device_view: <EyeOutlined style={{ color: '#1890ff' }} />,
      user_edit: <EditOutlined style={{ color: '#722ed1' }} />,
      password_change: <LockOutlined style={{ color: '#eb2f96' }} />,
      system_access: <SecurityScanOutlined style={{ color: '#13c2c2' }} />,
    };
    return icons[action] || <FileTextOutlined style={{ color: '#d9d9d9' }} />;
  };

  const handleEdit = () => {
    const roleName = user.role?.name || user.role;
    setSelectedRole(roleName);
    const customerIds = (user.customers || customers || []).map(c => c.id);
    form.setFieldsValue({
      ...user,
      role: roleName,
      customer_ids: customerIds,
    });
    setEditModalVisible(true);
  };

  const handleEditSubmit = async (values) => {
    try {
      await axios.put(`/api/v1/users/${id}`, values);
      message.success('Kullanıcı güncellendi');
      setEditModalVisible(false);
      fetchUserData();
    } catch (error) {
      handleApiError(error, 'Güncelleme başarısız', message.error);
    }
  };

  const handlePasswordChange = async (values) => {
    try {
      await axios.put(`/api/v1/users/${id}`, { password: values.new_password });
      message.success('Şifre başarıyla değiştirildi');
      setPasswordModalVisible(false);
      passwordForm.resetFields();
    } catch (error) {
      handleApiError(error, 'Şifre değiştirme başarısız', message.error);
    }
  };

  const handleDelete = async () => {
    Modal.confirm({
      title: 'Kullanıcıyı silmek istediğinizden emin misiniz?',
      content: 'Bu işlem geri alınamaz.',
      okText: 'Evet',
      cancelText: 'İptal',
      onOk: async () => {
        try {
          await axios.delete(`/api/v1/users/${id}`);
          message.success('Kullanıcı silindi');
          navigate('/users');
        } catch (error) {
          message.error('Silme işlemi başarısız');
        }
      },
    });
  };

  const getRolePermissions = (role) => {
    if (!role) return [];
    // If role is an object with permissions array, use it
    if (typeof role === 'object' && Array.isArray(role.permissions)) {
      return role.permissions.map(p => typeof p === 'object' ? p.display_name || p.name : p);
    }
    // Fallback: show role display name
    const roleName = typeof role === 'object' ? role.display_name || role.name : role;
    return roleName ? [roleName] : [];
  };

  if (loading) {
    return <div>Yükleniyor...</div>;
  }

  if (!user) {
    return <Alert message="Kullanıcı bulunamadı" type="error" />;
  }

  const activityColumns = [
    {
      title: 'Zaman',
      dataIndex: 'timestamp',
      key: 'timestamp',
      render: (timestamp) => new Date(timestamp).toLocaleString('tr-TR'),
      sorter: (a, b) => new Date(a.timestamp) - new Date(b.timestamp),
    },
    {
      title: 'İşlem',
      dataIndex: 'action',
      key: 'action',
      render: (action) => (
        <Space>
          {getActionIcon(action)}
          <Tag color="blue">{action}</Tag>
        </Space>
      ),
      filters: [
        { text: 'LOGIN', value: 'LOGIN' },
        { text: 'LOGOUT', value: 'LOGOUT' },
        { text: 'CREATE', value: 'CREATE' },
        { text: 'UPDATE', value: 'UPDATE' },
        { text: 'DELETE', value: 'DELETE' },
        { text: 'PASSWORD_CHANGE', value: 'PASSWORD_CHANGE' },
      ],
      onFilter: (value, record) => record.action === value,
    },
    {
      title: 'Kaynak',
      dataIndex: 'resource',
      key: 'resource',
      render: (resource) => resource ? <Tag color="green">{resource}</Tag> : '-',
    },
    {
      title: 'Durum',
      dataIndex: 'success',
      key: 'success',
      render: (success) => (
        <Badge 
          status={success === 'true' ? 'success' : 'error'} 
          text={success === 'true' ? 'Başarılı' : 'Başarısız'} 
        />
      ),
    },
    {
      title: 'IP Adresi',
      dataIndex: 'ip_address',
      key: 'ip_address',
      render: (ip) => ip || '-',
    },
    {
      title: 'Detaylar',
      dataIndex: 'details',
      key: 'details',
      render: (details) => details ? (
        <Tooltip title={JSON.stringify(details, null, 2)}>
          <Button type="link" size="small">Detay</Button>
        </Tooltip>
      ) : '-',
    },
  ];

  // Gerçek aktivite verilerini hesapla
  const getActivityStats = () => {
    if (!Array.isArray(activityLogs)) return [];
    
    const actionCounts = {};
    activityLogs.forEach(log => {
      actionCounts[log.action] = (actionCounts[log.action] || 0) + 1;
    });
    
    const colors = ['#52c41a', '#1890ff', '#fa8c16', '#722ed1', '#f5222d', '#13c2c2'];
    return Object.entries(actionCounts).map(([action, count], index) => ({
      name: action,
      value: count,
      color: colors[index % colors.length]
    }));
  };

  const roleActivityData = getActivityStats();

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/users')}>
            Geri
          </Button>
          <Title level={2} style={{ margin: 0 }}>
            {user.full_name}
          </Title>
          {getRoleTag(user.role)}
          <Badge status={user.is_active ? 'success' : 'error'} text={user.is_active ? 'Aktif' : 'Pasif'} />
        </Space>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={fetchUserData}>
            Yenile
          </Button>
          <Button icon={<EditOutlined />} onClick={handleEdit}>
            Düzenle
          </Button>
          <Button icon={<LockOutlined />} onClick={() => setPasswordModalVisible(true)}>
            Şifre Değiştir
          </Button>
          <Button icon={<DeleteOutlined />} danger onClick={handleDelete}>
            Sil
          </Button>
        </Space>
      </div>

      {/* Statistics Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Toplam Giriş"
              value={activityLogs.filter(log => log.action === 'login').length}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
            <Progress percent={100} showInfo={false} strokeColor="#3f8600" size="small" />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Son Giriş"
              value={user.last_login ? new Date(user.last_login).toLocaleDateString('tr-TR') : 'Hiç giriş yapmamış'}
              prefix={<CalendarOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
            <Progress percent={100} showInfo={false} strokeColor="#1890ff" size="small" />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Aktif Oturum"
              value={user.is_active ? 'Evet' : 'Hayır'}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: user.is_active ? '#52c41a' : '#ff4d4f' }}
            />
            <Progress percent={100} showInfo={false} strokeColor={user.is_active ? '#52c41a' : '#ff4d4f'} size="small" />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Rol Seviyesi"
              value={user.role?.hierarchy_level || 1}
              suffix="/5"
              prefix={<SecurityScanOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
            <Progress
              percent={((user.role?.hierarchy_level || 1) / 5) * 100}
              showInfo={false}
              strokeColor="#722ed1"
              size="small"
            />
          </Card>
        </Col>
      </Row>

      {/* Main Content */}
      <Tabs defaultActiveKey="overview">
        <TabPane tab="Genel Bakış" key="overview">
          <Row gutter={16}>
            <Col span={12}>
              <Card title="Kullanıcı Bilgileri">
                <Descriptions column={1}>
                  <Descriptions.Item label="Kullanıcı Adı">{user.username}</Descriptions.Item>
                  <Descriptions.Item label="Ad Soyad">{user.full_name}</Descriptions.Item>
                  <Descriptions.Item label="E-posta">{user.email}</Descriptions.Item>
                  <Descriptions.Item label="Rol">{getRoleTag(user.role)}</Descriptions.Item>
                  <Descriptions.Item label="Durum">
                    <Badge status={user.is_active ? 'success' : 'error'} text={user.is_active ? 'Aktif' : 'Pasif'} />
                  </Descriptions.Item>
                  <Descriptions.Item label="Son Giriş">
                    {user.last_login ? new Date(user.last_login).toLocaleString('tr-TR') : 'Hiç giriş yapmamış'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Oluşturulma Tarihi">
                    {user.created_at ? new Date(user.created_at).toLocaleDateString('tr-TR') : '-'}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            </Col>
            <Col span={12}>
              <Card title="Rol Yetkileri">
                <List
                  dataSource={getRolePermissions(user.role)}
                  renderItem={(permission) => (
                    <List.Item>
                      <Space>
                        <CheckCircleOutlined style={{ color: '#52c41a' }} />
                        <Text>{permission}</Text>
                      </Space>
                    </List.Item>
                  )}
                />
              </Card>
            </Col>
          </Row>
        </TabPane>

        <TabPane tab="Aktivite Logları" key="activity">
          <Card title="Kullanıcı Aktivite Geçmişi">
            <Table
              columns={activityColumns}
              dataSource={Array.isArray(activityLogs) ? activityLogs : []}
              rowKey="id"
              pagination={{ pageSize: 10 }}
              size="small"
            />
          </Card>
        </TabPane>

        <TabPane tab="İstatistikler" key="stats">
          <Row gutter={16}>
            <Col span={8}>
              <Card title="Aktivite Dağılımı">
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={roleActivityData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {roleActivityData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </Card>
            </Col>
            <Col span={8}>
              <Card title="Aktivite İstatistikleri">
                <Row gutter={16}>
                  <Col span={12}>
                    <Statistic
                      title="Toplam Aktivite"
                      value={Array.isArray(activityLogs) ? activityLogs.length : 0}
                      prefix={<HistoryOutlined />}
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="Başarılı İşlem"
                      value={Array.isArray(activityLogs) ? activityLogs.filter(log => log.success === 'true').length : 0}
                      prefix={<CheckCircleOutlined />}
                      valueStyle={{ color: '#3f8600' }}
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="Başarısız İşlem"
                      value={Array.isArray(activityLogs) ? activityLogs.filter(log => log.success === 'false').length : 0}
                      prefix={<ExclamationCircleOutlined />}
                      valueStyle={{ color: '#cf1322' }}
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="Son Aktivite"
                      value={Array.isArray(activityLogs) && activityLogs.length > 0 ? 
                        new Date(Math.max(...activityLogs.map(log => new Date(log.timestamp)))).toLocaleDateString('tr-TR') : 
                        'Yok'
                      }
                      prefix={<ClockCircleOutlined />}
                    />
                  </Col>
                </Row>
              </Card>
            </Col>
            <Col span={8}>
              <Card title="Son Aktiviteler">
                <Timeline>
                  {Array.isArray(activityLogs) ? activityLogs.slice(0, 5).map((log) => (
                    <Timeline.Item
                      key={log.id}
                      dot={getActionIcon(log.action)}
                      color={log.success === 'true' ? 'green' : 'red'}
                    >
                      <p><strong>{log.action}</strong></p>
                      <p><Text type="secondary">{log.resource}</Text></p>
                      <Text type="secondary">{new Date(log.timestamp).toLocaleString('tr-TR')}</Text>
                    </Timeline.Item>
                  )) : (
                    <Timeline.Item>
                      <Text type="secondary">Aktivite bulunamadı</Text>
                    </Timeline.Item>
                  )}
                </Timeline>
              </Card>
            </Col>
          </Row>
        </TabPane>

        <TabPane tab="Güvenlik" key="security">
          <Row gutter={16}>
            <Col span={8}>
              <Card title="Şifre Politikası">
                <List>
                  <List.Item>
                    <Space>
                      <LockOutlined />
                      <Text>Minimum Uzunluk: 8 karakter</Text>
                    </Space>
                  </List.Item>
                  <List.Item>
                    <Space>
                      <SecurityScanOutlined />
                      <Text>Büyük harf gerekli</Text>
                    </Space>
                  </List.Item>
                  <List.Item>
                    <Space>
                      <KeyOutlined />
                      <Text>Küçük harf gerekli</Text>
                    </Space>
                  </List.Item>
                  <List.Item>
                    <Space>
                      <ExclamationCircleOutlined />
                      <Text>Sayı gerekli</Text>
                    </Space>
                  </List.Item>
                  <List.Item>
                    <Space>
                      <GlobalOutlined />
                      <Text>Özel karakter gerekli</Text>
                    </Space>
                  </List.Item>
                </List>
              </Card>
            </Col>
            <Col span={8}>
              <Card title="Oturum Bilgileri">
                <List>
                  <List.Item>
                    <Space>
                      <ClockCircleOutlined />
                      <Text>Son Giriş: {user?.last_login ? safeFormatTimestamp(user.last_login) : 'Hiç giriş yapmamış'}</Text>
                    </Space>
                  </List.Item>
                  <List.Item>
                    <Space>
                      <UserOutlined />
                      <Text>Kullanıcı Durumu: {user?.is_active ? 'Aktif' : 'Pasif'}</Text>
                    </Space>
                  </List.Item>
                  <List.Item>
                    <Space>
                      <CalendarOutlined />
                      <Text>Hesap Oluşturma: {user?.created_at ? safeFormatTimestamp(user.created_at, 'Europe/Istanbul', { year: 'numeric', month: '2-digit', day: '2-digit' }) : '-'}</Text>
                    </Space>
                  </List.Item>
                  <List.Item>
                    <Space>
                      <HistoryOutlined />
                      <Text>Son Güncelleme: {user?.updated_at ? safeFormatTimestamp(user.updated_at, 'Europe/Istanbul', { year: 'numeric', month: '2-digit', day: '2-digit' }) : '-'}</Text>
                    </Space>
                  </List.Item>
                </List>
              </Card>
            </Col>
            <Col span={8}>
              <Card title="Son Giriş Bilgileri">
                {Array.isArray(activityLogs) && activityLogs.length > 0 ? (
                  <List>
                    {activityLogs
                      .filter(log => log.action === 'LOGIN')
                      .slice(0, 3)
                      .map((log, index) => (
                        <List.Item key={log.id}>
                          <List.Item.Meta
                            title={`Giriş ${index + 1}`}
                            description={
                              <Space direction="vertical" size="small">
                                <Text type="secondary">IP: {log.ip_address || 'Bilinmiyor'}</Text>
                                <Text type="secondary">Tarih: {new Date(log.timestamp).toLocaleString('tr-TR')}</Text>
                                <Text type="secondary">Durum: {log.success === 'true' ? 'Başarılı' : 'Başarısız'}</Text>
                              </Space>
                            }
                          />
                        </List.Item>
                      ))}
                  </List>
                ) : (
                  <Text type="secondary">Giriş bilgisi bulunamadı</Text>
                )}
              </Card>
            </Col>
          </Row>
        </TabPane>

        {/* Customer Role: Show assigned customers */}
        {user?.role?.name === 'customer' && (
          <TabPane
            tab={
              <span>
                <TeamOutlined />
                Atanmış Müşteriler ({customers.length})
              </span>
            }
            key="customers"
          >
            <Card
              title="Bu Kullanıcının Erişebildiği Müşteriler"
              extra={
                <Alert
                  message="Müşteri rolündeki kullanıcılar sadece atandıkları müşterilerin verilerini görebilir"
                  type="info"
                  showIcon
                  style={{ marginBottom: 0 }}
                />
              }
            >
              <Table
                columns={[
                  {
                    title: 'Müşteri Adı',
                    dataIndex: 'name',
                    key: 'name',
                    render: (text, record) => (
                      <Space>
                        <TeamOutlined style={{ color: record.is_active ? '#52c41a' : '#d9d9d9' }} />
                        <span style={{ fontWeight: 'bold' }}>{text}</span>
                      </Space>
                    ),
                  },
                  {
                    title: 'Durum',
                    dataIndex: 'is_active',
                    key: 'is_active',
                    render: (active) => (
                      <Badge
                        status={active ? 'success' : 'error'}
                        text={active ? 'Aktif' : 'Pasif'}
                      />
                    ),
                  },
                  {
                    title: 'İşlemler',
                    key: 'actions',
                    render: (_, record) => (
                      <Space>
                        <Tooltip title="Müşteri Profilini Görüntüle">
                          <Button
                            type="primary"
                            size="small"
                            icon={<EyeOutlined />}
                            onClick={() => navigate(`/customers/${record.id}`)}
                          >
                            Detay
                          </Button>
                        </Tooltip>
                      </Space>
                    ),
                  },
                ]}
                dataSource={customers}
                rowKey="id"
                loading={customersLoading}
                pagination={{ pageSize: 10 }}
                locale={{ emptyText: 'Bu kullanıcıya atanmış müşteri bulunmuyor' }}
              />
            </Card>
          </TabPane>
        )}
      </Tabs>

      {/* Edit Modal */}
      <Modal
        title="Kullanıcı Düzenle"
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form form={form} onFinish={handleEditSubmit} layout="vertical">
          <Form.Item
            name="username"
            label="Kullanıcı Adı"
            rules={usernameRules()}
          >
            <Input prefix={<UserOutlined />} disabled />
          </Form.Item>

          <Form.Item
            name="full_name"
            label="Ad Soyad"
            rules={fullNameRules()}
          >
            <Input placeholder="Ad Soyad" />
          </Form.Item>

          <Form.Item
            name="email"
            label="E-posta"
            rules={emailRules({ required: true })}
          >
            <Input prefix={<MailOutlined />} placeholder="ornek@email.com" />
          </Form.Item>

          <Form.Item
            name="role"
            label="Rol"
            rules={[{ required: true, message: 'Rol gerekli' }]}
          >
            <Select
              placeholder="Rol seçin"
              onChange={(value) => {
                setSelectedRole(value);
                if (value !== 'customer') {
                  form.setFieldsValue({ customer_ids: [] });
                }
              }}
            >
              {roles
                .filter(r => r.is_active)
                .filter(r => currentUser?.role?.name === 'admin' || r.name !== 'admin')
                .sort((a, b) => b.hierarchy_level - a.hierarchy_level)
                .map(r => (
                  <Option key={r.name} value={r.name}>
                    <Space>
                      <Badge color={r.color || '#d9d9d9'} />
                      {r.display_name}
                    </Space>
                  </Option>
                ))}
            </Select>
          </Form.Item>

          {selectedRole === 'customer' && (
            <>
              <Alert
                message="Müşteri Rolü Bilgisi"
                description="Bu kullanıcı sadece seçtiğiniz müşterilere ait cihazları ve verileri görebilecektir."
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
              <Form.Item
                name="customer_ids"
                label="Müşteriler"
                rules={[
                  {
                    required: true,
                    message: 'Müşteri rolü için en az bir müşteri seçilmeli'
                  }
                ]}
                help="Bu kullanıcının erişebileceği müşterileri seçin (birden fazla seçilebilir)"
              >
                <Select
                  mode="multiple"
                  placeholder="Müşteri seçin"
                  showSearch
                  filterOption={(input, option) =>
                    (option.children?.props?.children?.[1] || '').toLowerCase().includes(input.toLowerCase())
                  }
                  maxTagCount={3}
                  maxTagPlaceholder={(omittedValues) => `+${omittedValues.length} müşteri`}
                >
                  {availableCustomers.map(c => (
                    <Option key={c.id} value={c.id}>
                      <Space>
                        <TeamOutlined style={{ color: c.is_active ? '#52c41a' : '#d9d9d9' }} />
                        {c.name}
                        {!c.is_active && <Tag color="default">Pasif</Tag>}
                      </Space>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </>
          )}

          <Form.Item
            name="is_active"
            label="Hesap Durumu"
            valuePropName="checked"
          >
            <Switch checkedChildren="Aktif" unCheckedChildren="Pasif" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Güncelle
              </Button>
              <Button onClick={() => setEditModalVisible(false)}>
                İptal
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Password Change Modal */}
      <Modal
        title="Şifre Değiştir"
        open={passwordModalVisible}
        onCancel={() => setPasswordModalVisible(false)}
        footer={null}
      >
        <Form form={passwordForm} onFinish={handlePasswordChange} layout="vertical">
          <Form.Item
            name="new_password"
            label="Yeni Şifre"
            rules={passwordRules(passwordPolicy, { required: true })}
            extra={passwordHelpText(passwordPolicy)}
          >
            <Input.Password prefix={<LockOutlined />} />
          </Form.Item>

          <Form.Item
            name="confirm_password"
            label="Şifre Tekrar"
            dependencies={['new_password']}
            rules={[
              { required: true, message: 'Şifre tekrarı gerekli' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('new_password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Şifreler eşleşmiyor'));
                },
              }),
            ]}
          >
            <Input.Password prefix={<LockOutlined />} />
          </Form.Item>
          
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Şifre Değiştir
              </Button>
              <Button onClick={() => setPasswordModalVisible(false)}>
                İptal
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UserProfile;
